/**
 * ProgressIndicator Component
 *
 * Visual step indicator showing current stage in the import wizard.
 * Displays numbered dots with labels for each stage.
 *
 * @module components/import/stages
 */

import React from 'react';

import { ImportStage } from '@/components/import/types';
import { cn } from '@/utils/classname';

/**
 * Props for ProgressIndicator component
 */
export interface ProgressIndicatorProps {
  /** Current active stage */
  currentStage: ImportStage;
  /** Whether to show the validation stage (based on action config) */
  showValidation: boolean;
}

/**
 * Stage definition for display
 */
interface StageInfo {
  key: ImportStage;
  label: string;
}

/**
 * ProgressIndicator Component
 *
 * Shows a horizontal progress indicator with numbered steps.
 * Active step is highlighted, completed steps could be styled differently.
 *
 * @param props - Component props
 * @returns Progress indicator JSX
 */
export function ProgressIndicator({
  currentStage,
  showValidation,
}: ProgressIndicatorProps) {
  // Define stages to display
  const stages: StageInfo[] = [
    { key: ImportStage.SELECT_SOURCE, label: 'Select Source' },
    { key: ImportStage.UPLOAD_FILE, label: 'Upload File' },
    ...(showValidation
      ? [{ key: ImportStage.VALIDATE, label: 'Validate' }]
      : []),
  ];

  return (
    <div className="mb-6 flex items-center justify-center gap-2">
      {stages.map((stage, index) => (
        <React.Fragment key={stage.key}>
          {/* Stage indicator */}
          <div className="flex items-center gap-2">
            {/* Number circle */}
            <div
              className={cn(
                'flex size-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                currentStage === stage.key
                  ? 'bg-chariot-brand text-white'
                  : 'bg-chariot-background-secondary text-chariot-text-secondary'
              )}
            >
              {index + 1}
            </div>
            {/* Stage label */}
            <span
              className={cn(
                'text-sm transition-colors',
                currentStage === stage.key
                  ? 'text-chariot-text-primary font-medium'
                  : 'text-chariot-text-secondary'
              )}
            >
              {stage.label}
            </span>
          </div>

          {/* Connector line between stages */}
          {index < stages.length - 1 && (
            <div className="bg-chariot-stroke h-px w-12" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
